import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import useActionbarAuthStore from "src/store/useActionbarAuthStore";
import { downloadExcel, downloadSettingExcel } from "src/utils/blob/blobUtil";

import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { validateString } from "src/utils/string/stringUtil";
import { showSuccess, ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { deletePRD2010_R01, downloadExcel_PRD2010_R01, getPdfBlob_PRD2010_R01, getPRD2010_R01AllCount, getPRD2010_R01List, patchPRD2010_R01, postPRD2010_R01, postPRD2010_R01More } from "./Api_PRD2010_R01";
import { defaultPRD2010_R01Dto, PRD2010_R01Dto } from "./Dto_PRD2010_R01";
import { defaultPRD2010_R01Filter, PRD2010_R01Filter, usePRD2010_R01Store } from "./Store_PRD2010_R01";
import { AgGridReact } from "ag-grid-react";
import { fetchDataSetHandler, fetchDataSetHandler_refetch, fetchFilterConfig, InsertRowHandler, UpdateRowHandler } from "src/utils/grid/GridHandlerContent";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { validateEmployeeCd } from "src/utils/grid/GridValidateContent";
import useUiuxStore from "src/store/useUiuxStore";
import { useQzPrint } from "src/hooks/useQzPrint";
import qz from "qz-tray";
import { useCommonCodeValidate } from 'src/utils/commonCode/CommonCodeValidateContext';
import {
	buildCommonCodeRequirements,
	PRD2010_R01_HEADERROW_VALIDATE,
} from 'src/utils/commonCode/commonCodeRequirementConfig';


interface OrderContentProps {
	gridRef: React.RefObject<AgGridReact<PRD2010_R01Dto> | null>;
	setMessage: (message: string) => void;  
	
}	

type FetchMessageMode = 'show' | 'none';

interface FetchOptions {
	messageMode?: FetchMessageMode;
}


export const Content_PRD2010_R01 = (props: OrderContentProps) => {

	const { 
		gridRef,
		setMessage,
	} = props;

	const {
		PRD2010_R01Filter, setPRD2010_R01Filter,
		PRD2010_R01PastFilter, setPRD2010_R01PastFilter,
		PRD2010_R01List, setPRD2010_R01List, 
		insertPRD2010_R01List, removePRD2010_R01List,
		deletedPRD2010_R01List, addDeletedPRD2010_R01List, removeDeletedPRD2010_R01List,
		selectedPRD2010_R01List, setSelectedPRD2010_R01List, removeSelectedPRD2010_R01List,
		originalPRD2010_R01List, setOriginalPRD2010_R01List,
		allCount, setAllCount,
		initData,
		setScrollTop,
	} = usePRD2010_R01Store();

	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { setConfirmModalVisible, setConfirmModalProps } = useUiuxStore();
	const { validateCommonCodeLists } = useCommonCodeValidate();
	const { compCdList, employeeCdList, jobTeamList, ordTypeList, workPlaceList, itemCdMap } = useCommonCodeStore();
	const { printPdfFromBlob } = useQzPrint();

	const [clickable, setClickable] = useState(true);


	const setAuth = useActionbarAuthStore((state) => state.setAuth);
	
	
	const handleExcelClick = useCallback(async () => {

		const response = await downloadExcel_PRD2010_R01(
			'',
			getParams(PRD2010_R01Filter),
			PRD2010_R01List.length ?? 0
		);

		downloadSettingExcel({ responseData: response.data.data, fileName: '작업지시정보등록-excel.xlsx' });
	}, [PRD2010_R01Filter, PRD2010_R01List]);


	const handlePrintClick = useCallback(async () => {
		if (selectedPRD2010_R01List.length === 0) {
			showWarning("선택된 작업지시내역이 없습니다.");
			return;
		}

		const selectedPRD2010_R01ProductOrderNoList = PRD2010_R01List.filter(
			item => selectedPRD2010_R01List.includes(item.valueIndex as any)
		);

		setLoadingSpinner(true);
		try {
			const blob = await getPdfBlob_PRD2010_R01(selectedPRD2010_R01ProductOrderNoList[0].productOrderNo);

			if (qz.websocket.isActive()) {
				const ok = await printPdfFromBlob(blob, "report1");
				if (ok) {
					showSuccess("PDF 출력을 완료하였습니다.");
				} else {
					const fileURL = URL.createObjectURL(blob);
					window.open(fileURL, "_blank");
					setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
				}
			} else {
				const fileURL = URL.createObjectURL(blob);
				window.open(fileURL, "_blank");
				setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
				showSuccess("PDF를 새 탭에서 열었습니다.");
			}
		} catch (error) {
			setMessage("PDF 출력 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}

	}, [PRD2010_R01List, selectedPRD2010_R01List, printPdfFromBlob, setLoadingSpinner, setMessage]);

	// 권한 정보 로드
	useFetchActionbarAuth("PRD2010_R01");

	const getParams = (filter: Partial<PRD2010_R01Filter>) => {
		return {
			compCd: filter.compCd,
			workplaceCd: filter.workplaceCd,
			jobTeam: filter.jobTeam,
			dateRange: filter.dateRange,
			closeTag: filter.closeTag,
		}
	}

	const renewalAllCount = useCallback(async () => {
		const response = await getPRD2010_R01AllCount(getParams(PRD2010_R01Filter));
		setAllCount(response.data.data);
		return response.data.data;
	}, [PRD2010_R01Filter, setAllCount]);

	const executeDataFetch = useCallback(async (
		fetchType: 'search' | 'refetch',
		options?: FetchOptions
	) => {
		const messageMode = options?.messageMode ?? 'show';
		setLoadingSpinner(true);
		if (messageMode === 'show') {
			setScrollTop(null);
		}
	
		let initialListLength = PRD2010_R01List.length;
		// 저장 후 재조회 시에는 목록을 비우기.
		if (fetchType === 'refetch') {
			setPRD2010_R01List([]);
		}

		const responseData = await getPRD2010_R01List(
			'',
			getParams(PRD2010_R01Filter),
			fetchType === 'refetch' ? initialListLength : PRD2010_R01List.length ?? 0
		);
	
		let newList = [...responseData.data.data ] as PRD2010_R01Dto[];
		setPRD2010_R01List(newList);
	
		const processAllCount = await renewalAllCount();
		setOriginalPRD2010_R01List(newList);
		setSelectedPRD2010_R01List([]);
		removeDeletedPRD2010_R01List(deletedPRD2010_R01List);
		if (messageMode === 'show') {
			gridRef.current?.api.ensureIndexVisible(0, 'top');
		}
	
		if (newList.length === 0) {
			// insertPRD2010_R01List([{ ...defaultPRD2010_R01Row, inserted: true }], 0);
			if (messageMode === 'show') {
				showWarning('데이터가 없습니다.');
			}
		} else {
			if (messageMode === 'show') {
				ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
			}
		}
	
		setPRD2010_R01PastFilter(PRD2010_R01Filter);
		setLoadingSpinner(false);
	}, [
		PRD2010_R01Filter,  PRD2010_R01PastFilter, PRD2010_R01List, deletedPRD2010_R01List,
		gridRef, setMessage, setLoadingSpinner,
		setOriginalPRD2010_R01List, setSelectedPRD2010_R01List, insertPRD2010_R01List,
		setScrollTop,
	]);

	/**
	 * 사용자 목록 조회 함수
	 * 검색 조건에 맞는 사용자 목록을 가져옴
	 */
	const fetchData = useCallback(async () => {
		await executeDataFetch('search');
	}, [executeDataFetch]);

	// 버튼 비활성화 상태 관리
	const [buttonDisabled, setButtonDisabled] = useState({
		fetch: false,      // 조회: 기본 활성화
		create: false,     // 생성: 기본 활성화
		delete: true,      // 삭제: 기본 비활성화
		save: true,        // 저장: 기본 비활성화
		print: true,       // 출력: 기본 비활성화
		excel: true,       // 엑셀: 기본 비활성화
		init: false        // 초기화: 항상 활성화
	});

	const validateInsertData = (data: PRD2010_R01Dto, currentRowIndex: number) => {
		// if (!validateString(data.basOrderNo || '')) { showWarning("작업지시에 필요한 수주번호를 입력해주세요."); return false; }
        if (!validateString(data.compCd || '')) { showWarning("사업장을 선택해주세요."); return false; }
        if (!validateString(data.orderDt?.toString() || '')) { showWarning("작업일자를 선택해주세요."); return false; }
        if (!validateString(data.orderType || '')) { showWarning("작업지시유형을 선택해주세요."); return false; }
        if (!validateString(data.workplaceCd || '')) { showWarning("작업공정을 선택해주세요."); return false; }
        if (!validateString(data.jobTeam || '')) { showWarning("작업팀을 선택해주세요."); return false; }
        if (!validateString(data.orderEmp || '')) { showWarning("작업자를 선택해주세요."); return false; }
		if (!validateEmployeeCd(data.orderEmp, employeeCdList)) {
			showWarning('작업자가 존재하지 않습니다.(입력칸을 더블클릭하거나 엔터키로 세부사항을 열어서 지정해주세요)'); return false;
		}
        if (!validateString(data.workProcess || '')) { showWarning("공정유형을 선택해주세요."); return false; }
        if (!validateString(data.itemCd || '')) { showWarning("품목을 선택해주세요."); return false; }
		if (!itemCdMap.has(data.itemCd)) {
			showWarning('품목코드가 해당 MES의 품목정보에 존재하지 않습니다.\n품목코드: ' + data.itemCd); return false;
		}
		if (data.orderQty < 1) { showWarning("작업지시수량은 1개 이상이어야 합니다."); return false; }
		
		return true;
	}
	
	//신규 행 추가시 기본적으로 세팅할 데이터를 정하고 생성
	const defaultPRD2010_R01Row = useCallback(() => {
		const workPlaceCdListToUse = workPlaceList.filter(opt => opt.useYn === 'Y' && opt.orderYn === 'Y');

		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(PRD2010_R01_HEADERROW_VALIDATE, {
				compCdList,
				employeeCdList,
				jobTeamList,
				ordTypeList,
				workPlaceList: workPlaceCdListToUse,
			}),
		);
		if (!commonResult.valid) { return { ...defaultPRD2010_R01Dto }; }

		return {
			...defaultPRD2010_R01Dto,
			compCd: compCdList[0].compCd,
			orderType: ordTypeList[0].minorCd,
			workplaceCd: workPlaceCdListToUse[0].workplaceCd,
			jobTeam: jobTeamList[0].minorCd,
			workProcess: workPlaceCdListToUse[0].workProcess,
			orderEmp: workPlaceCdListToUse[0].workEmpNo,
			orderEmpNm: employeeCdList.find(item => item.empNo === workPlaceCdListToUse[0].workEmpNo)?.empNm || '',
			inserted: true,
		};
	}, [
		defaultPRD2010_R01Dto, compCdList, ordTypeList, workPlaceList, jobTeamList,
		employeeCdList, validateCommonCodeLists,
	]);
	
	const initContainerData = useCallback(() => {
		initData();
	}, [initData]);

	const handleInsertClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		InsertRowHandler({
			event: e,
			dataList: PRD2010_R01List,
			rowData: { ... defaultPRD2010_R01Row(), valueIndex: -1 },
			insertRow: insertPRD2010_R01List,
			defaultRowData: defaultPRD2010_R01Row(),
			setClickable,
		});
	}, [PRD2010_R01List, insertPRD2010_R01List, setClickable, defaultPRD2010_R01Row]);

	/**
	 * 삭제 핸들러
	 * 삭제 표시된 행이 있으면 복구, 없으면 선택된 행을 삭제 표시
	 */
	const handleDeleteClick = useCallback(() => {

		let vaild = true;
		selectedPRD2010_R01List.forEach(value => {
			if(PRD2010_R01List.find(item => item.valueIndex === Number(value))?.closeTag === 'Y') {
				vaild = false;
				return;
			}
		});
		if(!vaild) { showWarning('마감여부가 Y인 작업지시는 삭제 불가입니다.'); return; }

		if (deletedPRD2010_R01List.length > 0) {
			// 삭제 표시된 행이 있으면 전체 복구
			removeDeletedPRD2010_R01List(deletedPRD2010_R01List);
			removeSelectedPRD2010_R01List();
			setPRD2010_R01List([...originalPRD2010_R01List]);
		} else if (selectedPRD2010_R01List.length > 0) {
			// 삭제 표시된 행이 없으면 선택된 행만 삭제 표시
			addDeletedPRD2010_R01List(selectedPRD2010_R01List);
			removeSelectedPRD2010_R01List(selectedPRD2010_R01List);
		}
	}, [selectedPRD2010_R01List, deletedPRD2010_R01List, addDeletedPRD2010_R01List, removeDeletedPRD2010_R01List, removeSelectedPRD2010_R01List, setPRD2010_R01List, PRD2010_R01List]);

	const confirmMoreProcess = useCallback((item: PRD2010_R01Dto) => {
		return new Promise<boolean>((resolve) => {
			const closeModal = () => {
				setConfirmModalVisible(false);
				setConfirmModalProps(undefined);
			};

			setConfirmModalProps({
				title: '하위 작업지시 생성',
				message: `작업번호: ${item.productOrderNo}\n작업품목명: ${item.itemNm}\n\n해당 작업품목의 하위 작업지시를 생성하시겠습니까?`,
				confirmText: '생성',
				cancelText: '취소',
				onConfirm: () => { closeModal(); resolve(true);	},
				onCancel: () => {
					closeModal();
					resolve(false);
				},
			});
			setConfirmModalVisible(true);
		});
	}, [setConfirmModalProps, setConfirmModalVisible]);

	// inserted된 응답만 처리해서 moreProcessTrigger가 Y인 경우, 사용자 확인 후 백엔드로 요청 보내기.
	const moreSaveProcess = useCallback(async (insertedResponse : PRD2010_R01Dto[]) => {
		const moreProcessTrigger = insertedResponse.filter(item => item.moreProcessTrigger === 'Y');

		for(const item of moreProcessTrigger) {
			const confirmed = await confirmMoreProcess(item);
			if(!confirmed) continue;

			await postPRD2010_R01More([item]);
		}
	}, [confirmMoreProcess]);
	
	const handleSaveClick = useCallback(async () => {

		// 신규 사용자 처리
		const insertedList: PRD2010_R01Dto[] = PRD2010_R01List.filter(row => 
			!deletedPRD2010_R01List.includes(row.productOrderNo) && 
			row.inserted === true
		);

		const modifiedList : PRD2010_R01Dto[] = PRD2010_R01List.filter(row => 
			!deletedPRD2010_R01List.includes(row.productOrderNo) && 
			row.isModified && !row.inserted
		);
		
        // 수정 사용자 유효성 검증
		let seenMenuSet = new Set<string>();
		let valid = true;
		modifiedList.forEach((value) => {
			let index = PRD2010_R01List.findIndex(Order => Order.productOrderNo === value.productOrderNo);
			if(!validateInsertData(value, index)) valid = false;
			seenMenuSet.add(value.productOrderNo);
		});
		if(!valid) { return; }
        // 신규 사용자 유효성 검증
		seenMenuSet = new Set<string>();
		valid = true;
		insertedList.forEach((value) => {
			let index = PRD2010_R01List.findIndex(Order => Order.productOrderNo === value.productOrderNo);
			if(!validateInsertData(value, index)) valid = false;
			seenMenuSet.add(value.productOrderNo);
		});
		if(!valid) { return; }

		const deleteStringList = deletedPRD2010_R01List.map(item => item.toString());
		//마감여부가 Y인 작업지시는 삭제 불가
		const deleteTargetList = PRD2010_R01List.filter(item => 
			deleteStringList.includes(item.valueIndex.toString())
			&& item.closeTag !== 'Y'
		);

		const [insertedResponse, modifiedResponse, deleteResponse] = await Promise.all([
			insertedList.length > 0 ? postPRD2010_R01(insertedList) : Promise.resolve(),
			modifiedList.length > 0 ? patchPRD2010_R01(modifiedList) : Promise.resolve(),
			deleteTargetList.length > 0 ? Promise.all(deleteTargetList.map(value =>  deletePRD2010_R01(value.productOrderNo) )) : Promise.resolve()
		]);

		// 각 작업이 수행되었는지 여부
		const hasInserted = insertedList.length > 0;
		const hasModified = modifiedList.length > 0;
		const hasDeleted = deleteTargetList.length > 0;

		const insertedResponseData = insertedResponse?.data.data.map(item => item as PRD2010_R01Dto) ?? [];

		// 각 작업의 성공 여부 판단 (작업을 안했으면 성공으로 간주)
		const isInsertSuccess = !hasInserted || (insertedResponse?.data.successFlag === true);
		const isModifySuccess = !hasModified || (modifiedResponse?.data.successFlag === true);
		const isDeleteSuccess = !hasDeleted || (deleteResponse?.every(item => item.data.successFlag === true) ?? false);

		if (isInsertSuccess && isModifySuccess && isDeleteSuccess) {
			if (hasInserted || hasModified) {
				showSuccess('작업지시정보 저장이 완료되었습니다.');
				setMessage('작업지시정보 저장이 완료되었습니다.');
			} else if (hasDeleted) {
				showSuccess('작업지시정보 삭제가 완료되었습니다.');
				setMessage('작업지시정보 삭제가 완료되었습니다.');
			}
			setTimeout(() => { setMessage(''); }, 5000);

			if(hasInserted) {
				await moreSaveProcess(insertedResponseData);
			}
			
			await executeDataFetch('refetch', { messageMode: 'none' });
		} else {
			// 실패한 작업이 있는 경우 (필요에 따라 에러 메시지 처리)
			setTimeout(() => { setMessage(''); }, 5000);
		}

	}, [PRD2010_R01List, deletedPRD2010_R01List, originalPRD2010_R01List, executeDataFetch, moreSaveProcess]);


	// 선택된 사용자와 삭제된 사용자가 변경될 때 버튼 상태 업데이트
	useEffect(() => {
		setButtonDisabled(prev => ({
			...prev,
			delete: deletedPRD2010_R01List.length === 0 && selectedPRD2010_R01List.length === 0,
			save: PRD2010_R01List.length === 0,
			print: PRD2010_R01List.length === 0,
			excel: PRD2010_R01List.length === 0
		}));
	}, [selectedPRD2010_R01List, deletedPRD2010_R01List, PRD2010_R01List]);

	return{
		clickable, setClickable,
		handleExcelClick,
		handlePrintClick,
		fetchData,
		defaultPRD2010_R01Row,
		initContainerData,
		handleInsertClick,
		handleDeleteClick,
		handleSaveClick,
		buttonDisabled,
	}
}
